import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import ExaminerRoom3D from "@/components/lingo/ExaminerRoom3D";
import api from "@/lib/api";
import {
  Mic, MicOff, Volume2, Play, Sparkles, Loader2, Award, Clock, FileText,
  ArrowRight, RotateCcw, Headphones
} from "lucide-react";
import { toast } from "sonner";

/** Stages: intro | part1 | part2-prep | part2-speak | part3 | evaluating | result */

export default function SpeakingRoom() {
  const [session, setSession] = useState(null); // { id, question, part, idx, total_in_part, cue_card }
  const [stage, setStage] = useState("welcome"); // welcome | examiner-speaking | listening | prep | evaluating | result
  const [audioLevel, setAudioLevel] = useState(0);
  const [isExaminerSpeaking, setIsExaminerSpeaking] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [maxRecordSeconds, setMaxRecordSeconds] = useState(120);
  const [prepSeconds, setPrepSeconds] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [loading, setLoading] = useState(false);
  const [evalResult, setEvalResult] = useState(null);
  const [cueCard, setCueCard] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [partLabel, setPartLabel] = useState("");
  const [history, setHistory] = useState([]); // [{part, q, transcript}]

  // Audio + recorder refs
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const audioElRef = useRef(null);
  const rafRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const startTsRef = useRef(0);
  const timerRef = useRef(null);
  const prepTimerRef = useRef(null);
  const recordTimeoutRef = useRef(null);
  // Mic analyser refs (parallel pipeline so equalizer bars dance with student's voice)
  const micCtxRef = useRef(null);
  const micRafRef = useRef(null);
  const micNodesRef = useRef({ source: null, analyser: null });

  // Clean up on unmount
  useEffect(() => {
    return () => {
      cancelAnimationFrame(rafRef.current);
      cancelAnimationFrame(micRafRef.current);
      clearInterval(timerRef.current);
      clearInterval(prepTimerRef.current);
      clearTimeout(recordTimeoutRef.current);
      try { audioCtxRef.current?.close(); } catch {}
      try { micCtxRef.current?.close(); } catch {}
      try { mediaRef.current?.stream?.getTracks().forEach((t) => t.stop()); } catch {}
    };
  }, []);

  // -------- Helpers --------
  const speakText = useCallback(async (text) => {
    setIsExaminerSpeaking(true);
    setStage("examiner-speaking");
    try {
      // Fetch TTS audio as blob (Bearer auth via interceptor)
      const r = await api.post("/ai/tts", { text, voice: "nova" }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      // Setup analyser for lip-sync
      const audio = new Audio(url);
      audioElRef.current = audio;
      const ctx = audioCtxRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      // Resume if suspended (browsers gate audio behind user gesture)
      if (ctx.state === "suspended") {
        try { await ctx.resume(); } catch {}
      }
      const source = ctx.createMediaElementSource(audio);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyser.connect(ctx.destination);
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        analyser.getByteTimeDomainData(data);
        // RMS of the time-domain waveform — better than frequency for "is voice present"
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128; // -1..1
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length); // 0..~1
        // Apply a gentle expander so quiet speech still shows movement
        const level = Math.min(1, rms * 3.2);
        setAudioLevel(level);
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
      await audio.play();
      await new Promise((resolve) => { audio.onended = resolve; audio.onerror = resolve; });
      cancelAnimationFrame(rafRef.current);
      // Disconnect analyser + source to prevent node accumulation on the shared ctx
      try { source.disconnect(); } catch {}
      try { analyser.disconnect(); } catch {}
      setAudioLevel(0);
    } catch (e) {
      console.error("TTS error", e);
      // proceed silently — fall through
    } finally {
      setIsExaminerSpeaking(false);
    }
  }, []);

  // -------- Start session --------
  const startSession = async () => {
    setLoading(true);
    try {
      const r = await api.post("/speaking-room/start");
      setSession(r.data);
      setCurrentQuestion(r.data.question);
      setPartLabel(`Part 1 — Question ${r.data.question_idx + 1} of ${r.data.total_in_part}`);
      setStage("examiner-speaking");
      // Examiner: intro + first question
      await speakText(r.data.examiner_intro);
      await speakText(r.data.question);
      // After speaking, prompt the candidate to record
      setStage("listening");
      setMaxRecordSeconds(45);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start session");
    } finally {
      setLoading(false);
    }
  };

  // -------- Recording --------
  const startMicAnalyser = (stream) => {
    try {
      const ctx = micCtxRef.current || new (window.AudioContext || window.webkitAudioContext)();
      micCtxRef.current = ctx;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser); // do NOT connect to destination — would echo
      micNodesRef.current = { source, analyser };
      const data = new Uint8Array(analyser.fftSize);
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        const level = Math.min(1, rms * 3.6);
        setAudioLevel(level);
        micRafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      console.warn("Mic analyser unavailable", e);
    }
  };

  const stopMicAnalyser = () => {
    cancelAnimationFrame(micRafRef.current);
    try { micNodesRef.current.source?.disconnect(); } catch {}
    try { micNodesRef.current.analyser?.disconnect(); } catch {}
    micNodesRef.current = { source: null, analyser: null };
    setAudioLevel(0);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const m = new MediaRecorder(stream);
      mediaRef.current = m;
      chunksRef.current = [];
      m.ondataavailable = (e) => chunksRef.current.push(e.data);
      m.onstop = () => {
        clearInterval(timerRef.current);
        clearTimeout(recordTimeoutRef.current);
        stopMicAnalyser();
        stream.getTracks().forEach((t) => t.stop());
      };
      m.start();
      startTsRef.current = Date.now();
      setRecordSeconds(0);
      setRecording(true);
      // Live mic → equalizer
      startMicAnalyser(stream);
      timerRef.current = setInterval(() => {
        const secs = Math.round((Date.now() - startTsRef.current) / 1000);
        setRecordSeconds(secs);
        if (secs >= maxRecordSeconds) stopRecording();
      }, 250);
      // hard cap
      recordTimeoutRef.current = setTimeout(() => stopRecording(), (maxRecordSeconds + 1) * 1000);
    } catch (e) {
      toast.error("Microphone permission denied");
    }
  };

  const stopRecording = () => {
    if (!recording) return;
    setRecording(false);
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
    // process after a tick to let onstop run
    setTimeout(() => submitAnswer(), 250);
  };

  const submitAnswer = async () => {
    setLoading(true);
    setStage("examiner-speaking");
    try {
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      const file = new File([blob], "answer.webm", { type: "audio/webm" });
      const fd = new FormData();
      fd.append("file", file);
      fd.append("duration_sec", String(recordSeconds));
      const r = await api.post(`/speaking-room/${session.session_id}/submit-answer`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = r.data;
      setTranscript(data.transcript);
      setHistory((h) => [...h, { part: session?.part || 1, q: currentQuestion, transcript: data.transcript }]);

      if (data.is_complete) {
        await finalizeSession();
        return;
      }

      // Update state to next question
      setSession((s) => ({ ...s, part: data.next_part, question_idx: data.next_question_idx, question: data.next_question }));
      setCurrentQuestion(data.next_question || "");

      if (data.next_part === 2 && data.cue_card) {
        setCueCard(data.cue_card);
        setPartLabel("Part 2 — Long Turn");
        // Examiner introduces Part 2
        await speakText(
          "Now, I'd like you to talk about a particular topic. You'll have one minute to think about what you're going to say. You can make some notes if you wish. Do you understand?"
        );
        await speakText(data.cue_card.spoken || `Here is your topic. ${data.cue_card.topic}`);
        // Prep phase
        setStage("prep");
        const prepDur = data.prep_seconds || 60;
        setPrepSeconds(prepDur);
        prepTimerRef.current = setInterval(() => {
          setPrepSeconds((s) => {
            if (s <= 1) {
              clearInterval(prepTimerRef.current);
              // Auto-transition to recording
              setStage("listening");
              setMaxRecordSeconds(data.speak_seconds || 120);
              speakText("Alright, please begin speaking now. Remember, you have up to two minutes.").then(() => {
                setStage("listening");
              });
              return 0;
            }
            return s - 1;
          });
        }, 1000);
      } else {
        // Update part label
        if (data.next_part === 1) {
          setPartLabel(`Part 1 — Question ${data.next_question_idx + 1} of ${session.total_in_part}`);
          setMaxRecordSeconds(45);
        } else if (data.next_part === 3) {
          setPartLabel(`Part 3 — Discussion Q${data.next_question_idx + 1}`);
          setMaxRecordSeconds(60);
          if (data.next_question_idx === 0) {
            await speakText("Thank you. We've been talking about that topic. I'd like to discuss with you one or two more general questions related to this.");
          }
        }
        await speakText(data.next_question);
        setStage("listening");
      }
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to submit answer");
      setStage("listening");
    } finally {
      setLoading(false);
    }
  };

  const finalizeSession = async () => {
    setStage("evaluating");
    setLoading(true);
    try {
      await speakText("Thank you. That is the end of the speaking test. I'll now prepare your evaluation.");
      const r = await api.post(`/speaking-room/${session.session_id}/finalize`);
      setEvalResult(r.data);
      setStage("result");
    } catch (e) {
      toast.error("Evaluation failed");
      setStage("listening");
    } finally {
      setLoading(false);
    }
  };

  const skipPrep = () => {
    clearInterval(prepTimerRef.current);
    setPrepSeconds(0);
    setStage("listening");
    setMaxRecordSeconds(120);
  };

  const restart = () => {
    setSession(null);
    setStage("welcome");
    setTranscript("");
    setEvalResult(null);
    setCueCard(null);
    setHistory([]);
    setCurrentQuestion("");
    setPartLabel("");
  };

  // -------- UI --------
  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <DashboardLayout hideHeader>
      <div className="grid lg:grid-cols-[1fr_420px] gap-6 h-[calc(100vh-150px)] min-h-[640px]">
        {/* 3D scene */}
        <div className="relative rounded-3xl overflow-hidden border border-slate-200 bg-gradient-to-b from-slate-100 to-slate-200" data-testid="examiner-3d-stage">
          <ExaminerRoom3D
            audioLevel={audioLevel}
            isSpeaking={isExaminerSpeaking}
            stage={stage}
            recording={recording}
            loading={loading}
          />

          {/* Top overlay: title + part */}
          <div className="absolute top-0 left-0 right-0 p-5 flex items-start justify-between pointer-events-none z-10">
            <div className="px-4 py-2 rounded-2xl bg-black/40 backdrop-blur-md border border-white/10">
              <div className="font-display text-lg text-white tracking-tight">Virtual IELTS Speaking Room</div>
              <div className="text-[11px] text-cyan-200/80 mt-0.5 tracking-wider">with AI EXAMINER · SARAH</div>
            </div>
            {partLabel && (
              <div className="bg-[#F77F00] text-white px-4 py-2 rounded-full text-xs font-semibold shadow-lg shadow-rose-900/40 border border-white/10">
                {partLabel}
              </div>
            )}
          </div>

          {/* Center overlay: VOICE EQUALIZER when examiner is speaking */}
          {/* Removed — the 3D equalizer + state badge inside ExaminerRoom3D communicate this. */}

          {/* Center overlay: PROCESSING indicator */}
          {/* Removed — the state badge inside ExaminerRoom3D communicates this. */}

          {/* Bottom action bar */}
          <div className="absolute bottom-0 left-0 right-0 p-5">
            {stage === "welcome" && (
              <div className="bg-white/95 backdrop-blur rounded-2xl border border-slate-200 p-5 flex items-center justify-between shadow-lg">
                <div>
                  <div className="font-display text-lg text-[#003049]">Ready for your simulated IELTS Speaking test?</div>
                  <div className="text-sm text-slate-600 mt-1">~10–14 minutes · Parts 1, 2, 3 · band score at the end</div>
                </div>
                <button
                  onClick={startSession}
                  disabled={loading}
                  data-testid="start-speaking-room"
                  className="px-6 py-3 rounded-full bg-[#F77F00] hover:bg-[#D26900] text-white font-medium inline-flex items-center gap-2 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  Begin Test
                </button>
              </div>
            )}

            {stage === "examiner-speaking" && (
              <div className="bg-white/95 backdrop-blur rounded-2xl border border-slate-200 p-4 flex items-center gap-3 shadow-lg">
                <div className="w-10 h-10 rounded-full bg-[#FFF4E5] flex items-center justify-center">
                  <Volume2 className="w-5 h-5 text-[#F77F00] animate-pulse" />
                </div>
                <div className="text-sm text-slate-700">Listen carefully — wait for the examiner to finish before answering.</div>
              </div>
            )}

            {stage === "listening" && (
              <div className="bg-white/95 backdrop-blur rounded-2xl border border-slate-200 p-4 shadow-lg">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-[#F77F00] uppercase tracking-wider">Your turn</div>
                    <div className="text-sm text-slate-600 mt-0.5">
                      {recording ? "Speak naturally — fill the available time." : "Tap the mic and answer the examiner."}
                    </div>
                  </div>
                  {!recording ? (
                    <button onClick={startRecording} data-testid="answer-record-btn"
                      className="shrink-0 px-5 py-3 rounded-full bg-[#F77F00] hover:bg-[#D26900] text-white font-medium inline-flex items-center gap-2">
                      <Mic className="w-4 h-4" /> Start Speaking
                    </button>
                  ) : (
                    <button onClick={stopRecording} data-testid="answer-stop-btn"
                      className="shrink-0 px-5 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-medium inline-flex items-center gap-2">
                      <MicOff className="w-4 h-4" /> Stop & Submit
                    </button>
                  )}
                </div>
                {recording && (
                  <div className="mt-3 flex items-center gap-3">
                    <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
                      <div className="h-full bg-[#F77F00] transition-all" style={{ width: `${(recordSeconds / maxRecordSeconds) * 100}%` }} />
                    </div>
                    <div className="text-xs font-mono text-slate-600 tabular-nums">
                      {fmtTime(recordSeconds)} / {fmtTime(maxRecordSeconds)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {stage === "prep" && cueCard && (
              <div className="bg-white/95 backdrop-blur rounded-2xl border border-slate-200 p-5 shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#F77F00]" />
                    <div className="font-semibold text-sm text-[#003049]">Preparation Time</div>
                  </div>
                  <div className="font-mono text-2xl text-[#F77F00] tabular-nums">{fmtTime(prepSeconds)}</div>
                </div>
                <div className="rounded-xl border border-slate-200 p-4 bg-[#FFFAF5]">
                  <div className="text-sm font-semibold text-[#003049] mb-2">{cueCard.topic}</div>
                  <ul className="space-y-1 text-sm text-slate-700">
                    {(cueCard.bullets || []).map((b, i) => <li key={i}>• {b}</li>)}
                  </ul>
                </div>
                <button onClick={skipPrep} className="mt-3 text-xs text-slate-500 hover:text-[#F77F00]">
                  Skip preparation → start speaking now
                </button>
              </div>
            )}

            {stage === "evaluating" && (
              <div className="bg-white/95 backdrop-blur rounded-2xl border border-slate-200 p-5 flex items-center gap-3 shadow-lg">
                <Loader2 className="w-5 h-5 animate-spin text-[#F77F00]" />
                <div className="text-sm text-slate-700">Examiner is grading your performance across all 4 IELTS criteria…</div>
              </div>
            )}
          </div>
        </div>

        {/* Right rail */}
        <aside className="flex flex-col gap-4 overflow-hidden">
          {/* Progress card */}
          <div className="bg-white border border-slate-200 rounded-3xl p-5">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Progress</div>
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3].map((p) => {
                const active = session?.part === p;
                const done = (session?.part || 0) > p || stage === "result";
                return (
                  <div key={p} className={`rounded-xl p-3 text-center border ${
                    active ? "bg-[#F77F00] text-white border-[#F77F00]" :
                    done ? "bg-[#003049] text-white border-[#003049]" :
                    "bg-slate-50 text-slate-400 border-slate-200"
                  }`}>
                    <div className="text-[10px] uppercase tracking-wider opacity-80">Part</div>
                    <div className="font-display text-2xl">{p}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Live transcript / cue card */}
          {stage === "result" && evalResult ? (
            <ResultCard result={evalResult} onRestart={restart} />
          ) : cueCard && (stage === "prep" || (session?.part === 2 && stage === "listening")) ? (
            <div className="bg-white border border-slate-200 rounded-3xl p-5">
              <div className="flex items-center gap-2 text-xs font-semibold text-[#F77F00] uppercase tracking-wider mb-3">
                <FileText className="w-4 h-4" /> Cue Card
              </div>
              <div className="text-sm font-semibold text-[#003049] mb-2">{cueCard.topic}</div>
              <ul className="space-y-1.5 text-sm text-slate-700">
                {(cueCard.bullets || []).map((b, i) => <li key={i}>• {b}</li>)}
              </ul>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-3xl p-5 flex-1 overflow-y-auto">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Your Answers</div>
              {history.length === 0 ? (
                <div className="text-sm text-slate-400">Your transcribed answers will appear here as the test progresses.</div>
              ) : (
                <div className="space-y-3">
                  {history.map((h, i) => (
                    <div key={i} className="text-sm">
                      <div className="text-[11px] font-semibold text-[#F77F00] uppercase tracking-wider">Part {h.part}</div>
                      <div className="text-slate-800 mt-1">"{h.transcript}"</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Help footer */}
          <div className="bg-[#003049] text-white rounded-3xl p-5 text-sm">
            <div className="flex items-center gap-2 mb-2">
              <Headphones className="w-4 h-4" />
              <div className="font-semibold">Audio Tips</div>
            </div>
            <div className="text-slate-300 text-xs leading-relaxed">
              Use headphones for the best experience. Speak clearly into your microphone and try to fill the available time.
            </div>
          </div>
        </aside>
      </div>
    </DashboardLayout>
  );
}

function ResultCard({ result, onRestart }) {
  const nav = useNavigate();
  return (
    <div className="bg-white border border-slate-200 rounded-3xl p-5 flex-1 overflow-y-auto" data-testid="speaking-result">
      <div className="flex items-center gap-2 text-xs font-semibold text-[#F77F00] uppercase tracking-wider mb-3">
        <Award className="w-4 h-4" /> Final Band Score
      </div>
      <div className="font-display text-6xl text-[#003049]">{result.overall_band}</div>
      <div className="text-xs text-slate-500 mb-4">IELTS Overall (out of 9.0)</div>

      <div className="space-y-2 mb-4">
        {Object.entries(result.criteria || {}).map(([k, v]) => (
          <div key={k} className="rounded-xl border border-slate-200 p-3">
            <div className="flex justify-between items-center">
              <div className="text-xs font-semibold text-slate-600 capitalize">{k.replaceAll("_", " ")}</div>
              <div className="font-mono text-sm text-[#F77F00]">{v?.score}</div>
            </div>
            <div className="text-[11px] text-slate-500 mt-1 leading-snug">{v?.comment}</div>
          </div>
        ))}
      </div>

      <div className="text-xs text-slate-700 leading-relaxed mb-3">{result.feedback}</div>

      {result.suggestions?.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Action Items</div>
          <ul className="space-y-1.5 text-xs text-slate-700">
            {result.suggestions.map((s, i) => <li key={i}>• {s}</li>)}
          </ul>
        </div>
      )}

      {result.pronunciation_hotspots?.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Pronunciation Hotspots</div>
          <div className="flex flex-wrap gap-1.5">
            {result.pronunciation_hotspots.map((p, i) => (
              <span key={i} className="px-2.5 py-1 rounded-full bg-[#FFF4E5] text-[#F77F00] text-xs border border-[#FFE4E6]">{p}</span>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 mt-4">
        <button onClick={onRestart} data-testid="restart-speaking"
          className="flex-1 px-4 py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm inline-flex items-center justify-center gap-2">
          <RotateCcw className="w-4 h-4" /> Take again
        </button>
        <button onClick={() => nav("/app")}
          className="flex-1 px-4 py-2.5 rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm inline-flex items-center justify-center gap-2">
          Dashboard <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
